// lib/api.ts

import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { supabase } from "./supabase";

const API_BASE_URL = __DEV__
  ? "http://localhost:3001"
  : "https://rides.breakfastclubatx.com";

async function getAccessToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAccessToken();

  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Session expired");
    }
    const error = (await response.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    };
    throw new Error(error.error || error.message || "API error");
  }

  return response.json();
}

// Convenience methods for direct API calls
export const api = {
  get: <T>(endpoint: string) => apiFetch<T>(endpoint),

  post: <T>(endpoint: string, body: unknown) =>
    apiFetch<T>(endpoint, { method: "POST", body: JSON.stringify(body) }),

  put: <T>(endpoint: string, body: unknown) =>
    apiFetch<T>(endpoint, { method: "PUT", body: JSON.stringify(body) }),

  patch: <T>(endpoint: string, body: unknown) =>
    apiFetch<T>(endpoint, { method: "PATCH", body: JSON.stringify(body) }),

  delete: <T>(endpoint: string) => apiFetch<T>(endpoint, { method: "DELETE" }),
};

// ============================================
// React Query Hooks
// ============================================

/**
 * Generic hook for GET requests
 * @example
 * const { data, isLoading } = useApiQuery<User>(['user', userId], `/users/${userId}`);
 */
export function useApiQuery<T>(
  queryKey: readonly unknown[],
  endpoint: string,
  options?: Omit<UseQueryOptions<T, Error>, "queryKey" | "queryFn">
) {
  return useQuery<T, Error>({
    queryKey,
    queryFn: () => api.get<T>(endpoint),
    ...options,
  });
}

/**
 * Generic hook for POST mutations
 * @example
 * const { mutate, isPending } = useApiPost<User, CreateUserInput>('/users', {
 *   onSuccess: (data) => console.log('Created:', data),
 * });
 */
export function useApiPost<TData, TVariables>(
  endpoint: string,
  options?: Omit<UseMutationOptions<TData, Error, TVariables>, "mutationFn">
) {
  return useMutation<TData, Error, TVariables>({
    mutationFn: (variables) => api.post<TData>(endpoint, variables),
    ...options,
  });
}

/**
 * Generic hook for PUT mutations
 * @example
 * const { mutate } = useApiPut<User, UpdateUserInput>(`/users/${userId}`);
 */
export function useApiPut<TData, TVariables>(
  endpoint: string,
  options?: Omit<UseMutationOptions<TData, Error, TVariables>, "mutationFn">
) {
  return useMutation<TData, Error, TVariables>({
    mutationFn: (variables) => api.put<TData>(endpoint, variables),
    ...options,
  });
}

/**
 * Generic hook for PATCH mutations
 * @example
 * const { mutate } = useApiPatch<User, UpdateUserInput>(`/users/${userId}`);
 */
export function useApiPatch<TData, TVariables>(
  endpoint: string,
  options?: Omit<UseMutationOptions<TData, Error, TVariables>, "mutationFn">
) {
  return useMutation<TData, Error, TVariables>({
    mutationFn: (variables) => api.patch<TData>(endpoint, variables),
    ...options,
  });
}

/**
 * Generic hook for DELETE mutations
 * @example
 * const { mutate } = useApiDelete<void>(`/users/${userId}`);
 */
export function useApiDelete<TData = void>(
  endpoint: string,
  options?: Omit<UseMutationOptions<TData, Error, void>, "mutationFn">
) {
  return useMutation<TData, Error, void>({
    mutationFn: () => api.delete<TData>(endpoint),
    ...options,
  });
}

/**
 * Hook to get the query client for manual cache operations
 * @example
 * const queryClient = useApiQueryClient();
 * queryClient.invalidateQueries({ queryKey: ['users'] });
 */
export function useApiQueryClient() {
  return useQueryClient();
}
